/**
 * ModuleConfigModal Component
 *
 * @version 1.0
 * @updated 2026-03-19
 * @module core/layouts/components
 *
 * @description
 * Modal de configuración de módulo con layout de dos columnas.
 * Sidebar izquierdo muestra las entidades del módulo.
 * Panel derecho muestra los campos de configuración de la entidad seleccionada.
 *
 * @remarks
 * - Configuración es por proyecto, no por usuario
 * - Herencia: Entidad específica > Proyecto > Default global
 * - Indica visualmente cuando un valor es heredado vs específico
 */

import { useState, useEffect, useCallback } from 'react';
import { useModuleConfig } from '../hooks/useModuleConfig';
import type { ModuleId } from '../../module-store/types';
import type { ModuleEntityConfig } from '../../spatial/services/module-config-api.service';
import './module-config-modal.css';

/**
 * Props del componente ModuleConfigModal
 */
export interface ModuleConfigModalProps {
  /** ID del módulo a configurar */
  moduleId: ModuleId;
  /** ID del proyecto */
  projectId: string;
  /** Si el modal está abierto */
  isOpen: boolean;
  /** Callback al cerrar el modal */
  onClose: () => void;
  /** Nombre del módulo para mostrar en el header */
  moduleName?: string;
}


/**
 * Configuración de campos editables
 */
interface FieldConfig {
  key: keyof ModuleEntityConfig;
  label: string;
  description: string;
  type: 'number' | 'checkbox';
  unit?: string;
  min?: number;
  max?: number;
}

const FIELD_CONFIGS: FieldConfig[] = [
  {
    key: 'edgeThresholdMeters',
    label: 'Distancia maxima a segmento',
    description: 'Maxima distancia para asociar la entidad a un segmento de la red',
    type: 'number',
    unit: 'metros',
    min: 1,
    max: 100,
  },
  {
    key: 'nodeThresholdMeters',
    label: 'Distancia maxima a nodo',
    description: 'Maxima distancia para asociar la entidad a un nodo de la red',
    type: 'number',
    unit: 'metros',
    min: 1,
    max: 50,
  },
  {
    key: 'autoAssociateOnCreate',
    label: 'Auto-asociar al crear',
    description: 'Calcular asociacion automaticamente al crear una nueva entidad',
    type: 'checkbox',
  },
  {
    key: 'autoRecalculateOnNetworkChange',
    label: 'Recalcular al editar red',
    description: 'Actualizar asociacion cuando se modifica la red urbana',
    type: 'checkbox',
  },
  {
    key: 'allowMultipleConnections',
    label: 'Multiples conexiones',
    description: 'Permitir que una entidad se asocie a varios segmentos/nodos de la red',
    type: 'checkbox',
  },
  {
    key: 'autoConnectAllEdgesInRadius',
    label: 'Auto-conectar todos en radio',
    description: 'Al crear, conectar automaticamente a todos los segmentos dentro del umbral (solo si Multiples conexiones esta activo)',
    type: 'checkbox',
  },
];

/**
 * ModuleConfigModal Component
 */
export function ModuleConfigModal({
  moduleId,
  projectId,
  isOpen,
  onClose,
  moduleName = 'Módulo',
}: ModuleConfigModalProps): JSX.Element | null {
  const {
    config,
    isLoading,
    isSaving,
    error,
    updateEntity,
    resetEntity,
  } = useModuleConfig(projectId, moduleId);

  // Entidad seleccionada actualmente
  const [selectedEntity, setSelectedEntity] = useState<string | null>(null);

  // Estado local para edición (para controlar inputs)
  const [localConfig, setLocalConfig] = useState<ModuleEntityConfig>({});
  const [hasChanges, setHasChanges] = useState(false);

  // Seleccionar primera entidad al cargar
  useEffect(() => {
    if (config && !selectedEntity) {
      const entityTypes = Object.keys(config.entities);
      if (entityTypes.length > 0) {
        setSelectedEntity(entityTypes[0]);
      }
    }
  }, [config, selectedEntity]);

  // Actualizar localConfig cuando cambia la entidad seleccionada
  useEffect(() => {
    if (config && selectedEntity && config.entities[selectedEntity]) {
      setLocalConfig({ ...config.entities[selectedEntity].config });
      setHasChanges(false);
    }
  }, [config, selectedEntity]);

  /**
   * Maneja cambio de valor en un campo
   */
  const handleFieldChange = useCallback(
    (field: keyof ModuleEntityConfig, value: number | boolean | undefined) => {
      setLocalConfig((prev) => ({
        ...prev,
        [field]: value,
      }));
      setHasChanges(true);
    },
    [],
  );

  /**
   * Guarda los cambios de la entidad actual
   */
  const handleSave = useCallback(async () => {
    if (!selectedEntity || !hasChanges) return;

    try {
      await updateEntity(selectedEntity, localConfig);
      setHasChanges(false);
    } catch {
      // Error ya manejado en el hook
    }
  }, [selectedEntity, localConfig, hasChanges, updateEntity]);

  /**
   * Resetea la configuración de la entidad actual a valores por defecto
   */
  const handleReset = useCallback(async () => {
    if (!selectedEntity) return;

    try {
      await resetEntity(selectedEntity);
      setHasChanges(false);
    } catch {
      // Error ya manejado en el hook
    }
  }, [selectedEntity, resetEntity]);

  /**
   * Cierra el modal (con confirmación si hay cambios)
   */
  const handleClose = useCallback(() => {
    if (hasChanges) {
      const confirmed = window.confirm(
        'Hay cambios sin guardar. ¿Desea cerrar sin guardar?',
      );
      if (!confirmed) return;
    }
    setSelectedEntity(null);
    setLocalConfig({});
    setHasChanges(false);
    onClose();
  }, [hasChanges, onClose]);

  if (!isOpen) return null;

  const entityList = config ? Object.entries(config.entities) : [];
  const currentEntityInfo = selectedEntity && config?.entities[selectedEntity];

  return (
    <div className="module-config-overlay" onClick={handleClose}>
      <div
        className="module-config-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="module-config-title"
      >
        {/* Header */}
        <div className="module-config-header">
          <div className="module-config-header-left">
            <div className="module-config-icon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
            </div>
            <div>
              <h2 id="module-config-title" className="module-config-title">
                Configuración: {moduleName}
              </h2>
              <p className="module-config-subtitle">
                Ajustes de asociación punto-red por entidad
              </p>
            </div>
          </div>
          <button
            type="button"
            className="module-config-close"
            onClick={handleClose}
            aria-label="Cerrar configuración"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Body - Two Columns */}
        <div className="module-config-body">
          {/* Sidebar - Entity List */}
          <div className="module-config-sidebar">
            <h3 className="module-config-sidebar-title">Entidades</h3>
            <div className="entity-list">
              {isLoading ? (
                <div className="module-config-loading">
                  <div className="module-config-spinner" />
                </div>
              ) : entityList.length === 0 ? (
                <div className="module-config-empty">
                  No hay entidades configurables
                </div>
              ) : (
                entityList.map(([entityType, entityInfo]) => (
                  <button
                    key={entityType}
                    type="button"
                    className={`entity-item ${
                      selectedEntity === entityType ? 'active' : ''
                    }`}
                    onClick={() => {
                      if (hasChanges) {
                        const confirmed = window.confirm(
                          'Hay cambios sin guardar. ¿Desea cambiar de entidad sin guardar?',
                        );
                        if (!confirmed) return;
                      }
                      setSelectedEntity(entityType);
                      setHasChanges(false);
                    }}
                  >
                    <span className="entity-item-label">{entityInfo.label}</span>
                    {entityInfo.isCustom && (
                      <span
                        className="entity-item-custom"
                        title="Configuración personalizada"
                      />
                    )}
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Content Panel - Config Fields */}
          <div className="module-config-content">
            {error ? (
              <div className="module-config-error">
                <span className="module-config-error-icon">⚠️</span>
                <span>{error}</span>
              </div>
            ) : isLoading ? (
              <div className="module-config-loading">
                <div className="module-config-spinner" />
                <span>Cargando configuración...</span>
              </div>
            ) : !selectedEntity || !currentEntityInfo ? (
              <div className="module-config-empty">
                Seleccione una entidad para configurar
              </div>
            ) : (
              <>
                <div className="config-entity-header">
                  <h3 className="config-entity-title">
                    {currentEntityInfo.label}
                  </h3>
                  <button
                    type="button"
                    className="config-reset-btn"
                    onClick={handleReset}
                    disabled={!currentEntityInfo.isCustom || isSaving}
                    title={
                      currentEntityInfo.isCustom
                        ? 'Restaurar valores por defecto'
                        : 'Ya usa valores por defecto'
                    }
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                      <path d="M21 3v5h-5" />
                      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                      <path d="M3 21v-5h5" />
                    </svg>
                    Restaurar
                  </button>
                </div>

                <div className="config-fields">
                  {FIELD_CONFIGS.map((fieldConfig) => (
                    <div key={fieldConfig.key} className="config-field">
                      {fieldConfig.type === 'checkbox' ? (
                        <label className="config-field-checkbox">
                          <input
                            type="checkbox"
                            checked={localConfig[fieldConfig.key] as boolean ?? true}
                            onChange={(e) =>
                              handleFieldChange(fieldConfig.key, e.target.checked)
                            }
                            disabled={isSaving}
                          />
                          <span className="config-field-label">{fieldConfig.label}</span>
                        </label>
                      ) : (
                        <>
                          <label className="config-field-label">
                            {fieldConfig.label}
                          </label>
                          <div className="config-field-input-wrapper">
                            <input
                              type="number"
                              className="config-field-input"
                              value={localConfig[fieldConfig.key] as number ?? ''}
                              onChange={(e) => {
                                const value = e.target.value === ''
                                  ? undefined
                                  : Number(e.target.value);
                                handleFieldChange(fieldConfig.key, value);
                              }}
                              min={fieldConfig.min}
                              max={fieldConfig.max}
                              disabled={isSaving}
                            />
                            {fieldConfig.unit && (
                              <span className="config-field-unit">
                                {fieldConfig.unit}
                              </span>
                            )}
                          </div>
                        </>
                      )}
                      <p className="config-field-description">
                        {fieldConfig.description}
                      </p>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="module-config-footer">
          <button
            type="button"
            className="module-config-btn module-config-btn-secondary"
            onClick={handleClose}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="module-config-btn module-config-btn-primary"
            onClick={handleSave}
            disabled={!hasChanges || isSaving || isLoading}
          >
            {isSaving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}
